import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MENU_WIDTH = 180;
const ITEM_HEIGHT = 34;

/**
 * Actions dropdown that renders its menu in a portal on `document.body` with
 * `position: fixed`. Rendering outside the table's DOM is what keeps the menu
 * from being clipped/scrolled by an ancestor's `overflow` (the table lives in
 * an `overflow-x-auto` wrapper).
 */
const ActionsDropdown = ({ items }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuHeight = items.length * ITEM_HEIGHT + 12;
      const openUp = window.innerHeight - rect.bottom < menuHeight && rect.top > menuHeight;
      setCoords({
        top: openUp ? rect.top - menuHeight - 4 : rect.bottom + 4,
        left: Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const close = () => setOpen(false);

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-secondary btn-sm dropdown-toggle"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        Actions
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <ul
            ref={menuRef}
            className="dropdown-menu show"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              minWidth: MENU_WIDTH,
              zIndex: 1040,
              margin: 0,
            }}
          >
            {items.map((item) => (
              <li key={item.label}>
                <button
                  type="button"
                  className={`dropdown-item${item.danger ? " text-danger" : ""}`}
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
};

export default ActionsDropdown;
